import React from "react";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { useBackgroundNav } from "../contexts/backgroundNav";
import "./BackgroundNav.sass";

const EDGE_WIDTH = 80;
const SWIPE_THRESHOLD = 50;

const BackgroundNav: React.FC = () => {
  const { goNext, goPrev } = useBackgroundNav();
  const [showLeft, setShowLeft] = React.useState(false);
  const [showRight, setShowRight] = React.useState(false);
  const touchRef = React.useRef<{ startX: number } | null>(null);

  const hasNav = goNext !== null && goPrev !== null;
  const isDesktop = typeof window !== "undefined" && window.innerWidth >= 768;

  // Keyboard left/right arrow support on desktop
  React.useEffect(() => {
    if (!isDesktop || !hasNav) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === "ArrowLeft") goPrev!();
      else if (e.key === "ArrowRight") goNext!();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isDesktop, hasNav, goNext, goPrev]);

  // Desktop: edge hover
  const handleMouseMove = React.useCallback(
    (e: React.MouseEvent) => {
      const x = e.clientX;
      setShowLeft(x < EDGE_WIDTH);
      setShowRight(x > window.innerWidth - EDGE_WIDTH);
    },
    [],
  );

  const handleMouseLeave = React.useCallback(() => {
    setShowLeft(false);
    setShowRight(false);
  }, []);

  // Mobile: swipe
  const handleTouchStart = React.useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 1) {
      touchRef.current = { startX: e.touches[0].clientX };
    }
  }, []);

  const handleTouchEnd = React.useCallback(
    (e: React.TouchEvent) => {
      if (!touchRef.current) return;
      const deltaX = e.changedTouches[0].clientX - touchRef.current.startX;
      touchRef.current = null;
      if (deltaX > SWIPE_THRESHOLD) {
        goPrev?.();
      } else if (deltaX < -SWIPE_THRESHOLD) {
        goNext?.();
      }
    },
    [goNext, goPrev],
  );

  if (!hasNav) return null;

  if (!isDesktop) {
    return (
      <div
        className="BackgroundNav BackgroundNav--mobile"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      />
    );
  }

  return (
    <div
      className="BackgroundNav"
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <div
        className={`BackgroundNav__zone BackgroundNav__zone--left${showLeft ? " is-visible" : ""}`}
        onClick={() => goPrev?.()}
      >
        <LeftOutlined className="BackgroundNav__arrow" />
      </div>
      <div
        className={`BackgroundNav__zone BackgroundNav__zone--right${showRight ? " is-visible" : ""}`}
        onClick={() => goNext?.()}
      >
        <RightOutlined className="BackgroundNav__arrow" />
      </div>
    </div>
  );
};

export default BackgroundNav;
